import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, Home, Clock, Target, Trophy, BookOpen,
  CheckCircle, XCircle, ChevronRight, ChevronLeft, Flag,
  Brain, Zap, Play, Pause, RefreshCw, BarChart3, Award,
  Sparkles, Loader2, AlertCircle, TrendingUp, TrendingDown,
  Lightbulb, Shield, Rocket, ChartLine, Focus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSessionControl } from '../contexts/SessionControlContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Skill Level Colors
const SKILL_COLORS = {
  novice: { bg: 'bg-red-500', text: 'text-red-400', border: 'border-red-500/50' },
  developing: { bg: 'bg-orange-500', text: 'text-orange-400', border: 'border-orange-500/50' },
  competent: { bg: 'bg-yellow-500', text: 'text-yellow-400', border: 'border-yellow-500/50' },
  proficient: { bg: 'bg-green-500', text: 'text-green-400', border: 'border-green-500/50' },
  expert: { bg: 'bg-purple-500', text: 'text-purple-400', border: 'border-purple-500/50' }
};

// Zone Colors
const ZONE_COLORS = {
  reinforcement: { bg: 'bg-blue-600', icon: Shield },
  new_topics: { bg: 'bg-emerald-600', icon: Lightbulb },
  stretch: { bg: 'bg-purple-600', icon: Rocket },
  mixed_review: { bg: 'bg-amber-600', icon: RefreshCw }
};

// Focus Mode Options
const FOCUS_MODES = [
  { id: 'balanced', name: 'Balanced', desc: 'Mix of reinforcement and challenge', icon: Target },
  { id: 'weak_topics', name: 'Reinforce', desc: 'Focus on areas needing work', icon: Shield },
  { id: 'stretch', name: 'Challenge', desc: 'Push your limits', icon: Rocket }
];

const AdaptiveMock = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read user once so the mock-exam config defaults to the student's own
  // age group / exam goal — previously every login defaulted to 11-plus.
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();
  const initialStage = (() => {
    const yg = (currentUser?.year_group || currentUser?.yearGroup || currentUser?.studentProfile?.yearGroup || '').toLowerCase();
    const examGoal = (currentUser?.studentProfile?.examGoal || '').toLowerCase();
    // Match "year 10" / "year-10" / "year 11" / "year-11" before the broader
    // "year 1" check — otherwise "year 10" matches "year 1" by substring.
    if (/year[\s-]?1[01]\b/.test(yg) || examGoal === 'gcse') return 'gcse';
    if (/year[\s-]?[789]\b/.test(yg)) return 'year-9';
    if (/year[\s-]?[12]\b/.test(yg) || examGoal === '7+' || examGoal === '7plus') return '7-plus';
    return '11-plus';
  })();

  const [view, setView] = useState('home'); // 'home', 'config', 'exam', 'results'
  const [loading, setLoading] = useState(false);
  const [skillMatrix, setSkillMatrix] = useState(null);
  const [selectedFocus, setSelectedFocus] = useState('balanced');
  const [selectedStage, setSelectedStage] = useState(initialStage);
  const [selectedSubject, setSelectedSubject] = useState('maths');
  const [useAiVariation, setUseAiVariation] = useState(true);
  const [aiProvider, setAiProvider] = useState('openai');  // 'openai' or 'gemini'
  const [currentMock, setCurrentMock] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timePerQuestion, setTimePerQuestion] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [mockResults, setMockResults] = useState(null);
  const [mockHistory, setMockHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const questionStartTime = useRef(Date.now());

  const token = localStorage.getItem('token');
  const { startSession, endSession } = useSessionControl();

  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    loadInitialData();
  }, [token]);

  // Timer Effect
  useEffect(() => {
    let interval;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setIsTimerRunning(false);
            handleSubmitMock();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning]);

  // Track time per question
  useEffect(() => {
    if (view === 'exam' && currentMock?.questions) {
      const currentQ = currentMock.questions[currentQuestionIndex];
      if (currentQ) {
        questionStartTime.current = Date.now();
      }
    }
  }, [currentQuestionIndex, view]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [matrixRes, historyRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/adaptive-mock/skill-matrix`, {
          params: { stage: selectedStage, subject: selectedSubject },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/adaptive-mock/history`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { history: [] } })),
        axios.get(`${API}/adaptive-mock/analytics/summary`, {
          params: { stage: selectedStage, subject: selectedSubject },
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);

      setSkillMatrix(matrixRes.data.skill_matrix);
      setMockHistory(historyRes.data.history || []);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load skill data');
    } finally {
      setLoading(false);
    }
  };

  const generateAdaptiveMock = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/adaptive-mock/generate`, {
        stage: selectedStage,
        subject: selectedSubject,
        focus_mode: selectedFocus,
        question_count: 25,
        time_minutes: 45,
        use_ai_variation: useAiVariation,
        ai_provider: aiProvider
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCurrentMock(response.data);
      setAnswers({});
      setTimePerQuestion({});
      setFlaggedQuestions(new Set());
      setCurrentQuestionIndex(0);
      setTimeRemaining(response.data.duration_minutes * 60);
      setView('exam');
      setIsTimerRunning(true);
      questionStartTime.current = Date.now();
      toast.success('Adaptive mock generated for you!');
      
      // Start SSCS session for fatigue monitoring
      startSession('mock', selectedSubject);
    } catch (error) {
      console.error('Error generating adaptive mock:', error);
      toast.error('Failed to generate adaptive mock');
    } finally {
      setLoading(false);
    }
  };

  const recordQuestionTime = (questionId) => {
    const timeSpent = Math.round((Date.now() - questionStartTime.current) / 1000);
    setTimePerQuestion(prev => ({
      ...prev,
      [questionId]: (prev[questionId] || 0) + timeSpent
    }));
    questionStartTime.current = Date.now();
  };

  const handleAnswerSelect = (questionId, answerIndex) => {
    setAnswers(prev => ({ ...prev, [questionId]: answerIndex }));
  };

  const navigateQuestion = (direction) => {
    const currentQ = currentMock?.questions?.[currentQuestionIndex];
    if (currentQ) {
      recordQuestionTime(currentQ.question_id);
    }

    if (direction === 'next' && currentQuestionIndex < currentMock.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else if (direction === 'prev' && currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const jumpToQuestion = (index) => {
    const currentQ = currentMock?.questions?.[currentQuestionIndex];
    if (currentQ) {
      recordQuestionTime(currentQ.question_id);
    }
    setCurrentQuestionIndex(index);
  };

  const toggleFlag = (questionId) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleSubmitMock = async () => {
    setIsTimerRunning(false);
    const currentQ = currentMock?.questions?.[currentQuestionIndex];
    if (currentQ) {
      recordQuestionTime(currentQ.question_id);
    }

    // End SSCS session
    endSession();

    setLoading(true);
    try {
      const totalTime = (currentMock.duration_minutes * 60) - timeRemaining;
      
      const response = await axios.post(
        `${API}/adaptive-mock/submit/${currentMock.mock_id}`,
        {
          mock_id: currentMock.mock_id,
          answers: answers,
          time_per_question: timePerQuestion,
          flagged_questions: Array.from(flaggedQuestions),
          total_time_seconds: totalTime
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMockResults(response.data);
      setView('results');
      toast.success('Mock submitted! Check your personalized analysis.');
    } catch (error) {
      console.error('Error submitting mock:', error);
      toast.error('Failed to submit mock');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentQuestion = currentMock?.questions?.[currentQuestionIndex];
  const answeredCount = Object.keys(answers).length;
  const totalQuestions = currentMock?.total_questions || 0;

  // ============ HOME VIEW ============
  if (view === 'home') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" data-testid="adaptive-mock-home">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 py-6 px-4 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate(-1)} 
                className="text-white hover:bg-white/20"
                data-testid="back-btn"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/categories')} 
                className="text-white hover:bg-white/20"
                data-testid="home-btn"
              >
                <Home className="mr-2 h-4 w-4" /> Home
              </Button>
            </div>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-xl">
                <Brain className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">Adaptive Mock Engine</h1>
                <p className="text-pink-200">AI-powered personalized assessments</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Skill Matrix Card */}
              <Card className="bg-slate-800/50 border-slate-700 lg:col-span-1" data-testid="skill-matrix-card">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ChartLine className="h-5 w-5 text-purple-400" />
                    Your Skill Matrix
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {skillMatrix?.skills?.map((skill, idx) => (
                    <div key={idx} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300 capitalize">{skill.topic.replace(/_/g, ' ')}</span>
                        <Badge className={`${SKILL_COLORS[skill.skill_level]?.bg} text-white text-xs`}>
                          {skill.skill_level}
                        </Badge>
                      </div>
                      <Progress 
                        value={skill.score} 
                        className="h-2 bg-slate-700"
                      />
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>{skill.score}%</span>
                        <span className="flex items-center gap-1">
                          {skill.trend === 'improving' && <TrendingUp className="h-3 w-3 text-green-400" />}
                          {skill.trend === 'declining' && <TrendingDown className="h-3 w-3 text-red-400" />}
                          {skill.attempts} attempts
                        </span>
                      </div>
                    </div>
                  ))}

                  <div className="pt-4 border-t border-slate-700">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Overall Band</span>
                      <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                        {skillMatrix?.overall_band || 'Developing'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Generate Mock Card */}
              <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2" data-testid="generate-mock-card">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-pink-400" />
                    Generate Adaptive Mock
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    The engine analyzes your skill matrix and creates a personalized mock exam
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Focus Mode Selection - Mobile Responsive */}
                  <div>
                    <p className="text-sm text-slate-400 mb-3">Select Focus Mode</p>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      {FOCUS_MODES.map(mode => {
                        const Icon = mode.icon;
                        return (
                          <button
                            key={mode.id}
                            onClick={() => setSelectedFocus(mode.id)}
                            className={`p-2 sm:p-4 rounded-lg border transition-all ${
                              selectedFocus === mode.id
                                ? 'bg-purple-600/30 border-purple-500 ring-2 ring-purple-500'
                                : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                            }`}
                            data-testid={`focus-mode-${mode.id}`}
                          >
                            <Icon className={`h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 ${
                              selectedFocus === mode.id ? 'text-purple-400' : 'text-slate-400'
                            }`} />
                            <p className="text-white font-medium text-xs sm:text-sm">{mode.name}</p>
                            <p className="text-slate-500 text-[10px] sm:text-xs mt-0.5 sm:mt-1 hidden sm:block">{mode.desc}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Blueprint Preview - Mobile Responsive */}
                  <div className="bg-slate-700/30 rounded-lg p-3 sm:p-4">
                    <p className="text-sm text-slate-400 mb-3">Mock Blueprint Preview</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                      {Object.entries(ZONE_COLORS).map(([zone, config]) => {
                        const Icon = config.icon;
                        const percentage = zone === 'reinforcement' ? 
                          (selectedFocus === 'weak_topics' ? '60%' : selectedFocus === 'stretch' ? '20%' : '40%') :
                          zone === 'stretch' ? 
                          (selectedFocus === 'stretch' ? '40%' : selectedFocus === 'weak_topics' ? '10%' : '20%') :
                          zone === 'new_topics' ? '25%' : '15%';
                        
                        return (
                          <div key={zone} className="text-center p-2 sm:p-3 bg-slate-800/50 rounded-lg">
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full ${config.bg} mx-auto mb-1 sm:mb-2 flex items-center justify-center`}>
                              <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                            </div>
                            <p className="text-white text-xs sm:text-sm font-medium capitalize">{zone.replace('_', ' ')}</p>
                            <p className="text-slate-400 text-[10px] sm:text-xs">{percentage}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Subject Selection */}
                  <div className="flex gap-4">
                    <select
                      value={selectedStage}
                      onChange={(e) => setSelectedStage(e.target.value)}
                      className="flex-1 bg-slate-700 border-slate-600 text-white rounded-lg px-4 py-2"
                      data-testid="stage-select"
                    >
                      <option value="7-plus">7+ Entry</option>
                      <option value="11-plus">11+ Entry</option>
                      <option value="year-9">Year 9</option>
                      <option value="gcse">GCSE</option>
                    </select>
                    <select
                      value={selectedSubject}
                      onChange={(e) => setSelectedSubject(e.target.value)}
                      className="flex-1 bg-slate-700 border-slate-600 text-white rounded-lg px-4 py-2"
                      data-testid="subject-select"
                    >
                      <option value="maths">Mathematics</option>
                      <option value="english">English</option>
                    </select>
                  </div>

                  {/* AI Enhancement Settings */}
                  <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-lg p-4 border border-purple-500/30">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-pink-400" />
                        <span className="text-white font-medium text-sm">AI Question Generation</span>
                      </div>
                      <button
                        onClick={() => setUseAiVariation(!useAiVariation)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                          useAiVariation 
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                            : 'bg-slate-600/50 text-slate-400 border border-slate-500/50'
                        }`}
                        data-testid="ai-toggle-btn"
                      >
                        {useAiVariation ? 'Enabled' : 'Disabled'}
                      </button>
                    </div>
                    
                    {useAiVariation && (
                      <div className="space-y-3">
                        <p className="text-slate-400 text-xs">
                          AI generates unique question variations in the Stretch and New Topics zones
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setAiProvider('openai')}
                            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                              aiProvider === 'openai'
                                ? 'bg-emerald-600/30 border-emerald-500 text-emerald-400 border'
                                : 'bg-slate-700/50 border-slate-600 text-slate-400 border hover:border-slate-500'
                            }`}
                            data-testid="ai-openai-btn"
                          >
                            GPT-5.2
                          </button>
                          <button
                            onClick={() => setAiProvider('gemini')}
                            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                              aiProvider === 'gemini'
                                ? 'bg-blue-600/30 border-blue-500 text-blue-400 border'
                                : 'bg-slate-700/50 border-slate-600 text-slate-400 border hover:border-slate-500'
                            }`}
                            data-testid="ai-gemini-btn"
                          >
                            Gemini
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={generateAdaptiveMock}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    disabled={loading}
                    data-testid="generate-mock-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    Generate Adaptive Mock {useAiVariation && `(${aiProvider === 'openai' ? 'GPT-5.2' : 'Gemini'})`}
                  </Button>
                </CardContent>
              </Card>

              {/* Recent History */}
              <Card className="bg-slate-800/50 border-slate-700 lg:col-span-3" data-testid="mock-history-card">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-blue-400" />
                    Recent Adaptive Mocks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {mockHistory.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No adaptive mocks taken yet. Generate your first one above!</p>
                  ) : (
                    <div className="space-y-3">
                      {mockHistory.slice(0, 5).map((mock, idx) => (
                        <div key={idx} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              mock.score >= 80 ? 'bg-green-600' :
                              mock.score >= 60 ? 'bg-yellow-600' :
                              mock.score >= 40 ? 'bg-orange-600' : 'bg-red-600'
                            }`}>
                              <span className="text-white font-bold">{Math.round(mock.score)}%</span>
                            </div>
                            <div>
                              <p className="text-white font-medium">
                                {new Date(mock.submitted_at).toLocaleDateString()}
                              </p>
                              <p className="text-slate-400 text-sm">
                                {mock.correct_count}/{mock.total_questions} correct
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {mock.weak_topics_identified?.slice(0, 2).map(topic => (
                              <Badge key={topic} variant="outline" className="border-red-500/50 text-red-400 text-xs">
                                {topic}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============ EXAM VIEW ============
  if (view === 'exam' && currentMock && currentQuestion) {
    return (
      <div className="min-h-screen bg-slate-900" data-testid="adaptive-mock-exam">
        {/* Timer Header - Mobile Responsive */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 py-2 sm:py-3 px-3 sm:px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Badge className="bg-white/20 text-white text-[10px] sm:text-xs px-2 py-0.5">
                {currentMock.blueprint_summary?.focus_mode?.toUpperCase() || 'BALANCED'}
              </Badge>
              <span className="text-white/80 text-xs sm:text-sm">
                Q{currentQuestionIndex + 1}/{totalQuestions}
              </span>
            </div>
            <div className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1 sm:py-2 rounded-lg ${
              timeRemaining < 300 ? 'bg-red-500/30' : 'bg-white/10'
            }`}>
              <Clock className={`h-4 w-4 sm:h-5 sm:w-5 ${timeRemaining < 300 ? 'text-red-400' : 'text-white'}`} />
              <span className={`font-mono font-bold text-sm sm:text-base ${timeRemaining < 300 ? 'text-red-400' : 'text-white'}`}>
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
          {/* Question Navigator - Mobile: Horizontal scroll at top */}
          <div className="lg:hidden mb-4">
            <div className="overflow-x-auto pb-2 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="flex gap-1.5 min-w-max">
                {currentMock.questions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => jumpToQuestion(idx)}
                    className={`w-7 h-7 rounded text-xs font-medium transition-all flex-shrink-0 ${
                      idx === currentQuestionIndex
                        ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                        : answers[q.question_id] !== undefined
                        ? 'bg-green-600/30 text-green-400'
                        : flaggedQuestions.has(q.question_id)
                        ? 'bg-yellow-600/30 text-yellow-400'
                        : 'bg-slate-700 text-slate-400'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-4 gap-4 sm:gap-6">
            {/* Question Panel */}
            <div className="lg:col-span-3 space-y-6">
              {/* Zone Indicator */}
              <div className="flex items-center gap-2">
                {(() => {
                  const zoneConfig = ZONE_COLORS[currentQuestion.zone] || ZONE_COLORS.mixed_review;
                  const Icon = zoneConfig.icon;
                  return (
                    <>
                      <div className={`p-2 rounded-lg ${zoneConfig.bg}`}>
                        <Icon className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-slate-400 text-sm capitalize">
                        {currentQuestion.zone?.replace('_', ' ')} Zone
                      </span>
                      {currentQuestion.is_ai_generated && (
                        <Badge className="bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs">
                          <Sparkles className="h-3 w-3 mr-1" /> AI Generated
                        </Badge>
                      )}
                      <Badge variant="outline" className="border-slate-600 text-slate-400 ml-auto">
                        Difficulty: {currentQuestion.difficulty}/10
                      </Badge>
                    </>
                  );
                })()}
              </div>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white">Question {currentQuestionIndex + 1}</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleFlag(currentQuestion.question_id)}
                      className={flaggedQuestions.has(currentQuestion.question_id) ? 'text-yellow-400' : 'text-slate-400'}
                      data-testid="flag-question-btn"
                    >
                      <Flag className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-lg text-white leading-relaxed">
                    {currentQuestion.question_text}
                  </p>

                  <div className="space-y-3">
                    {currentQuestion.options.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleAnswerSelect(currentQuestion.question_id, idx)}
                        className={`w-full p-4 rounded-lg text-left transition-all ${
                          answers[currentQuestion.question_id] === idx
                            ? 'bg-purple-600 border-purple-500 text-white'
                            : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:bg-slate-700'
                        } border`}
                        data-testid={`option-${idx}`}
                      >
                        <span className="font-medium mr-3">
                          {String.fromCharCode(65 + idx)}.
                        </span>
                        {option}
                      </button>
                    ))}
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={() => navigateQuestion('prev')}
                    disabled={currentQuestionIndex === 0}
                    className="border-slate-600 text-slate-300"
                    data-testid="prev-question-btn"
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" /> Previous
                  </Button>
                  {currentQuestionIndex === totalQuestions - 1 ? (
                    <Button
                      onClick={handleSubmitMock}
                      className="bg-gradient-to-r from-green-600 to-emerald-600"
                      data-testid="submit-mock-btn"
                    >
                      Submit Mock <CheckCircle className="h-4 w-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => navigateQuestion('next')}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="next-question-btn"
                    >
                      Next <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </CardFooter>
              </Card>
            </div>

            {/* Question Navigator - Desktop only (mobile uses horizontal nav above) */}
            <div className="hidden lg:block lg:col-span-1">
              <Card className="bg-slate-800 border-slate-700 sticky top-4">
                <CardHeader className="py-3">
                  <CardTitle className="text-white text-sm">Navigator</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-2">
                    {currentMock.questions.map((q, idx) => (
                      <button
                        key={idx}
                        onClick={() => jumpToQuestion(idx)}
                        className={`w-8 h-8 rounded text-xs font-medium transition-all ${
                          idx === currentQuestionIndex
                            ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                            : answers[q.question_id] !== undefined
                            ? 'bg-green-600/30 text-green-400'
                            : flaggedQuestions.has(q.question_id)
                            ? 'bg-yellow-600/30 text-yellow-400'
                            : 'bg-slate-700 text-slate-400'
                        }`}
                        data-testid={`nav-q-${idx}`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                  </div>
                  <div className="mt-4 space-y-2 text-xs text-slate-400">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-green-600/30" />
                      <span>Answered ({answeredCount})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-yellow-600/30" />
                      <span>Flagged ({flaggedQuestions.size})</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ RESULTS VIEW ============
  if (view === 'results' && mockResults) {
    console.log('Mock Results:', mockResults);
    console.log('Results array:', mockResults.results);
    console.log('Wrong answers count:', mockResults.results?.filter(r => !r.is_correct).length);
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" data-testid="adaptive-mock-results">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 py-6 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-3xl font-bold text-white mb-2">Mock Complete!</h1>
            <div className="inline-flex items-center gap-3 bg-white/20 px-6 py-3 rounded-full">
              <Trophy className="h-6 w-6 text-yellow-400" />
              <span className="text-3xl font-bold text-white">{mockResults.score}%</span>
              <span className="text-white/70">({mockResults.correct_count}/{mockResults.total_questions})</span>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Error Analysis */}
            <Card className="bg-slate-800/50 border-slate-700" data-testid="error-analysis-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-400" />
                  Error Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-red-900/20 rounded-lg border border-red-500/30">
                    <p className="text-red-400 text-sm">Knowledge Gaps</p>
                    <p className="text-2xl font-bold text-white">{mockResults.error_classification?.knowledge_gaps || 0}</p>
                  </div>
                  <div className="p-4 bg-orange-900/20 rounded-lg border border-orange-500/30">
                    <p className="text-orange-400 text-sm">Conceptual Errors</p>
                    <p className="text-2xl font-bold text-white">{mockResults.error_classification?.conceptual_errors || 0}</p>
                  </div>
                  <div className="p-4 bg-yellow-900/20 rounded-lg border border-yellow-500/30">
                    <p className="text-yellow-400 text-sm">Careless Mistakes</p>
                    <p className="text-2xl font-bold text-white">{mockResults.error_classification?.careless_mistakes || 0}</p>
                  </div>
                  <div className="p-4 bg-blue-900/20 rounded-lg border border-blue-500/30">
                    <p className="text-blue-400 text-sm">Time Pressure</p>
                    <p className="text-2xl font-bold text-white">{mockResults.error_classification?.time_pressure_issues || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Topic Breakdown */}
            <Card className="bg-slate-800/50 border-slate-700" data-testid="topic-breakdown-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-400" />
                  Topic Performance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(mockResults.topic_breakdown || {}).map(([topic, data]) => {
                  const accuracy = Math.round((data.accuracy || 0) * 100);
                  return (
                    <div key={topic} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300 capitalize">{topic.replace(/_/g, ' ')}</span>
                        <span className={accuracy >= 70 ? 'text-green-400' : accuracy >= 50 ? 'text-yellow-400' : 'text-red-400'}>
                          {accuracy}%
                        </span>
                      </div>
                      <Progress 
                        value={accuracy} 
                        className={`h-2 ${accuracy >= 70 ? 'bg-green-900/30' : accuracy >= 50 ? 'bg-yellow-900/30' : 'bg-red-900/30'}`}
                      />
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Updated Skill Matrix */}
            <Card className="bg-slate-800/50 border-slate-700" data-testid="updated-skills-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-400" />
                  Skill Matrix Updated
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Your skills have been recalibrated based on this mock
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center gap-4 p-6 bg-slate-700/30 rounded-lg">
                  <div className="text-center">
                    <p className="text-slate-400 text-sm">New Band</p>
                    <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-lg px-4 py-1 mt-1">
                      {mockResults.updated_skill_matrix?.overall_band?.toUpperCase() || 'DEVELOPING'}
                    </Badge>
                  </div>
                </div>

                {mockResults.weak_topics?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-slate-400 mb-2">Areas to Focus</p>
                    <div className="flex flex-wrap gap-2">
                      {mockResults.weak_topics.map(topic => (
                        <Badge key={topic} variant="outline" className="border-red-500/50 text-red-400">
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {mockResults.strong_topics?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-slate-400 mb-2">Strengths</p>
                    <div className="flex flex-wrap gap-2">
                      {mockResults.strong_topics.map(topic => (
                        <Badge key={topic} variant="outline" className="border-green-500/50 text-green-400">
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recommendations */}
            <Card className="bg-slate-800/50 border-slate-700" data-testid="recommendations-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-yellow-400" />
                  Personalized Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {mockResults.recommendations?.map((rec, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg border ${
                      rec.priority === 'high' ? 'bg-red-900/20 border-red-500/30' :
                      rec.priority === 'medium' ? 'bg-yellow-900/20 border-yellow-500/30' :
                      'bg-green-900/20 border-green-500/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {rec.type === 'micro_lesson' && <BookOpen className="h-5 w-5 text-blue-400 flex-shrink-0" />}
                      {rec.type === 'practice' && <Target className="h-5 w-5 text-purple-400 flex-shrink-0" />}
                      {rec.type === 'strategy' && <Lightbulb className="h-5 w-5 text-yellow-400 flex-shrink-0" />}
                      {rec.type === 'celebration' && <Trophy className="h-5 w-5 text-green-400 flex-shrink-0" />}
                      <div>
                        <p className="text-white text-sm">{rec.message}</p>
                        {rec.action && (
                          <Button 
                            variant="link" 
                            className="p-0 h-auto text-purple-400 text-xs"
                            onClick={() => navigate(rec.action)}
                          >
                            Go to {rec.type === 'micro_lesson' ? 'Learning Engine' : 'Practice'} →
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Wrong Answers Review Section */}
          <Card className="bg-slate-800/50 border-slate-700 mt-6 border-2 border-red-500/30" data-testid="wrong-answers-card">
            <CardHeader className="bg-red-900/20">
              <CardTitle className="text-white flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-400" />
                Review Wrong Answers 
                {mockResults.results && (
                  <Badge variant="outline" className="ml-2 border-red-500/50 text-red-400">
                    {mockResults.results.filter(r => !r.is_correct).length} questions
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-slate-400">
                Learn from your mistakes to improve next time
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!mockResults.results ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-yellow-400 mx-auto mb-3" />
                  <p className="text-yellow-400 font-medium">Results data not available. Please try again.</p>
                </div>
              ) : mockResults.results.filter(r => !r.is_correct).length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
                  <p className="text-green-400 font-medium">Congratulations! You got all questions correct!</p>
                </div>
              ) : (
                mockResults.results.filter(r => !r.is_correct).map((result, idx) => {
                  // Use question_text and options directly from result (now returned by backend)
                  const questionText = result.question_text || 'Question not available';
                  const options = result.options || [];
                  return (
                    <div 
                      key={result.question_id} 
                      className="p-4 bg-slate-700/50 rounded-lg border border-red-500/20"
                      data-testid={`wrong-answer-${idx}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="border-red-500/50 text-red-400">
                            Q{idx + 1}
                          </Badge>
                          <Badge variant="outline" className="border-slate-500 text-slate-400 capitalize">
                            {result.topic?.replace(/_/g, ' ')}
                          </Badge>
                          <Badge 
                            variant="outline" 
                            className={`capitalize ${
                              result.difficulty === 'hard' ? 'border-purple-500/50 text-purple-400' :
                              result.difficulty === 'medium' ? 'border-yellow-500/50 text-yellow-400' :
                              'border-green-500/50 text-green-400'
                            }`}
                          >
                            {result.difficulty}
                          </Badge>
                        </div>
                        {result.was_flagged && (
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                            <Flag className="h-3 w-3 mr-1" /> Flagged
                          </Badge>
                        )}
                      </div>

                      {/* Question Text */}
                      <p className="text-white font-medium mb-4">
                        {questionText}
                      </p>

                      {/* Options */}
                      <div className="grid gap-2 mb-4">
                        {options.map((option, optIdx) => {
                          const isUserAnswer = optIdx === result.user_answer;
                          const isCorrectAnswer = optIdx === result.correct_answer;
                          
                          return (
                            <div
                              key={optIdx}
                              className={`p-3 rounded-lg border flex items-center gap-3 ${
                                isCorrectAnswer 
                                  ? 'bg-green-900/30 border-green-500/50' 
                                  : isUserAnswer 
                                    ? 'bg-red-900/30 border-red-500/50'
                                    : 'bg-slate-800/50 border-slate-600/50'
                              }`}
                            >
                              <span className={`font-bold ${
                                isCorrectAnswer ? 'text-green-400' : 
                                isUserAnswer ? 'text-red-400' : 'text-slate-400'
                              }`}>
                                {String.fromCharCode(65 + optIdx)}.
                              </span>
                              <span className={
                                isCorrectAnswer ? 'text-green-300' : 
                                isUserAnswer ? 'text-red-300' : 'text-slate-300'
                              }>
                                {option}
                              </span>
                              {isCorrectAnswer && (
                                <CheckCircle className="h-4 w-4 text-green-400 ml-auto" />
                              )}
                              {isUserAnswer && !isCorrectAnswer && (
                                <XCircle className="h-4 w-4 text-red-400 ml-auto" />
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Your Answer vs Correct Answer Summary */}
                      <div className="flex flex-wrap gap-4 mb-4 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400">Your answer:</span>
                          <span className="text-red-400 font-medium">
                            {result.user_answer !== null && result.user_answer !== undefined
                              ? String.fromCharCode(65 + result.user_answer)
                              : 'Not answered'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400">Correct answer:</span>
                          <span className="text-green-400 font-medium">
                            {String.fromCharCode(65 + result.correct_answer)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400">Time taken:</span>
                          <span className="text-blue-400 font-medium">
                            {result.time_taken ? `${Math.round(result.time_taken)}s` : 'N/A'}
                          </span>
                        </div>
                      </div>

                      {/* Explanation */}
                      {result.explanation && (
                        <div className="p-3 bg-blue-900/20 rounded-lg border border-blue-500/30">
                          <div className="flex items-center gap-2 mb-2">
                            <Lightbulb className="h-4 w-4 text-yellow-400" />
                            <span className="text-yellow-400 font-medium text-sm">Explanation</span>
                          </div>
                          <p className="text-slate-300 text-sm">{result.explanation}</p>
                        </div>
                      )}

                      {/* Step by Step Solution */}
                      {result.step_by_step && result.step_by_step.length > 0 && (
                        <div className="mt-3 p-3 bg-purple-900/20 rounded-lg border border-purple-500/30">
                          <div className="flex items-center gap-2 mb-2">
                            <BookOpen className="h-4 w-4 text-purple-400" />
                            <span className="text-purple-400 font-medium text-sm">Step-by-Step Solution</span>
                          </div>
                          <ol className="list-decimal list-inside space-y-1">
                            {result.step_by_step.map((step, stepIdx) => (
                              <li key={stepIdx} className="text-slate-300 text-sm">{step}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
            <Button
              variant="outline"
              onClick={() => {
                setView('home');
                loadInitialData();
              }}
              className="border-slate-600 text-slate-300"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
            </Button>
            <Button
              onClick={generateAdaptiveMock}
              className="bg-gradient-to-r from-purple-600 to-pink-600"
              data-testid="try-another-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Try Another Mock
            </Button>
            <Button
              onClick={() => navigate('/learning-engine')}
              className="bg-gradient-to-r from-blue-600 to-cyan-600"
              data-testid="go-to-learning-btn"
            >
              <Brain className="h-4 w-4 mr-2" /> Go to Learning Engine
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading fallback
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
    </div>
  );
};

export default AdaptiveMock;
